import type {
  CommandCenterViewModel,
  InitiativeStatus,
} from './mapCommandCenter';

export type ApprovalState = 'done' | 'current' | 'pending';

export interface ApprovalStepDef {
  id: string;
  label: string;
  cfo?: boolean;
  /** Shown on the current step when the user can advance manually */
  actionLabel?: string;
  /** Hint when this step is current */
  hint?: string;
}

export interface ApprovalStep {
  id: string;
  label: string;
  state: ApprovalState;
  cfo?: boolean;
  note?: string;
  actionLabel?: string;
}

export interface CfoAssumption {
  label: string;
  value: string;
  confidence: 'High' | 'Medium' | 'Low';
  source: string;
}

export interface CfoSummary {
  benefit: string;
  benefitNote?: string;
  cost: string;
  costNote?: string;
  payback: string;
  conf: string;
  assumptions: CfoAssumption[];
  flags: string[];
}

export const APPROVAL_STEP_DEFS: ApprovalStepDef[] = [
  {
    id: 'sponsor',
    label: 'Executive sponsor confirmed',
    // No actionLabel — completes automatically when an Executive Sponsor is assigned.
    hint: 'Assign an Executive Sponsor in Team to complete this stage.',
  },
  {
    id: 'business_case',
    label: 'Business case & ROI model submitted',
    actionLabel: 'Complete',
    hint: 'Submit the ROI and business case for finance review.',
  },
  {
    id: 'cfo',
    label: 'CFO review & sign-off',
    cfo: true,
    actionLabel: 'Complete',
    hint: 'Finance reviews and signs off on the numbers.',
  },
  {
    id: 'board',
    label: 'Board ratification',
    actionLabel: 'Complete',
    hint: 'Leadership formally ratifies the initiative.',
  },
  {
    id: 'kickoff',
    label: 'Initiative kickoff',
    actionLabel: 'Complete',
    hint: 'Start execution once approvals are complete.',
  },
];

/** @deprecated Prefer APPROVAL_STEP_DEFS — kept for compact status-derived views */
const DEFAULT_APPROVAL_STEPS = APPROVAL_STEP_DEFS;

export function hasAssignedSponsor(sponsor: string | null | undefined): boolean {
  if (sponsor == null) return false;
  const trimmed = sponsor.trim();
  if (!trimmed) return false;
  const normalized = trimmed.toLowerCase();
  if (
    normalized === '—'
    || normalized === '-'
    || normalized === 'unassigned'
    || normalized === 'not assigned'
    || normalized === 'n/a'
    || normalized === 'tbd'
  ) {
    return false;
  }
  return true;
}

/** True when Team includes someone with the Executive Sponsor role. */
export function teamHasExecutiveSponsor(
  team: { role?: string | null }[] | null | undefined,
): boolean {
  if (!team?.length) return false;
  return team.some((member) => (member.role ?? '').trim().toLowerCase() === 'executive sponsor');
}

/**
 * Number of completed steps (0–5). Current step index === completedCount when < 5.
 * Stage 1 (sponsor) is automatic — no Complete button. Stages 2–5 use Complete.
 */
export function approvalStepsFromProgress(completedCount: number): ApprovalStep[] {
  const capped = Math.max(0, Math.min(APPROVAL_STEP_DEFS.length, completedCount));

  return APPROVAL_STEP_DEFS.map((step, i) => {
    const state: ApprovalState =
      i < capped ? 'done' : i === capped ? 'current' : 'pending';

    return {
      id: step.id,
      label: step.label,
      cfo: step.cfo,
      state,
      note: state === 'current' ? step.hint : undefined,
      // Only stages with an explicit actionLabel get a Complete button (not sponsor).
      actionLabel: state === 'current' && step.actionLabel ? step.actionLabel : undefined,
    };
  });
}

function approvalStorageKey(clientId: string, useCaseId: string): string {
  // v2: mock always starts at stage 1 (ignores older auto-sponsor progress keys)
  return `dva.approvalSignoff.v2.${clientId}.${useCaseId}`;
}

export function loadApprovalProgress(clientId: string, useCaseId: string): number {
  try {
    const raw = localStorage.getItem(approvalStorageKey(clientId, useCaseId));
    if (raw == null) return 0;
    const n = Number.parseInt(raw, 10);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.min(APPROVAL_STEP_DEFS.length, n));
  } catch {
    return 0;
  }
}

export function saveApprovalProgress(clientId: string, useCaseId: string, completedCount: number): void {
  try {
    const capped = Math.max(0, Math.min(APPROVAL_STEP_DEFS.length, completedCount));
    localStorage.setItem(approvalStorageKey(clientId, useCaseId), String(capped));
  } catch {
    // ignore quota / private mode
  }
}

function approvalCurrentIndex(status: InitiativeStatus): number {
  switch (status) {
    case 'Approval Pending':
      return 0;
    case 'Approved':
      return 3;
    case 'Activation Required':
    case 'Planning':
      return 4;
    case 'On Hold':
      return 2;
    case 'Active':
    case 'At Risk':
    case 'Complete':
    case 'Value Tracking':
      return 5;
    default:
      return 0;
  }
}

/** Compact / portfolio views — still status-derived (read-only). */
export function approvalStepsFromStatus(status: InitiativeStatus): ApprovalStep[] {
  const current = approvalCurrentIndex(status);
  return DEFAULT_APPROVAL_STEPS.map((step, i) => ({
    id: step.id,
    label: step.label,
    cfo: step.cfo,
    state: (i < current ? 'done' : i === current ? 'current' : 'pending') as ApprovalState,
  }));
}

/** Pull "67%" style adoption targets from risk copy when KPIs omit them. */
function extractAdoptionRate(text: string | null | undefined): string | null {
  if (!text) return null;
  const patterns = [
    /target\s+adoption\s+rate\s+(\d+(?:\.\d+)?)\s*%?/i,
    /adoption\s+rate\s+(\d+(?:\.\d+)?)\s*%/i,
    /(\d+(?:\.\d+)?)\s*%\s*(?:adoption|target)/i,
  ];
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match?.[1]) return `${match[1]}%`;
  }
  return null;
}

function isBlankMetric(value: string | null | undefined): boolean {
  if (value == null) return true;
  const trimmed = value.trim();
  if (!trimmed) return true;
  const normalized = trimmed.toLowerCase();
  return (
    normalized === '—'
    || normalized === '-'
    || normalized === 'n/a'
    || normalized === 'tbd'
    || normalized === 'not yet measured'
  );
}

function findAdoptionMetric(viewModel: CommandCenterViewModel) {
  const rows = [
    ...viewModel.metrics.operational,
    ...viewModel.metrics.clinical,
    ...viewModel.metrics.financial,
    ...viewModel.metrics.delivery,
  ];
  return rows.find((row) => /adoption/i.test(row.label)) ?? null;
}

function formatAdoptionFlag(
  risk: CommandCenterViewModel['risks'][number],
  viewModel: CommandCenterViewModel,
): string {
  const adoptionMetric = findAdoptionMetric(viewModel);
  const actual = !isBlankMetric(adoptionMetric?.current)
    ? adoptionMetric!.current
    : 'Not yet measured';
  const target =
    (!isBlankMetric(adoptionMetric?.target) ? adoptionMetric!.target : null)
    ?? extractAdoptionRate(risk.description)
    ?? extractAdoptionRate(risk.mitigation)
    ?? null;

  if (!target) {
    return `${risk.title} — actual ${actual}; target not specified`;
  }
  return `${risk.title} — actual ${actual}, target ${target}`;
}

function cfoFlagsFromViewModel(viewModel: CommandCenterViewModel): string[] {
  return viewModel.risks
    .filter((risk) => risk.status === 'Open' && risk.severity === 'High')
    .map((risk) => {
      const mentionsAdoption =
        /adoption/i.test(risk.title)
        || /adoption/i.test(risk.description)
        || /adoption/i.test(risk.mitigation ?? '');
      return mentionsAdoption ? formatAdoptionFlag(risk, viewModel) : risk.title;
    });
}

export function cfoSummaryFromViewModel(viewModel: CommandCenterViewModel): CfoSummary {
  const confidence = viewModel.header.confidence.toLowerCase();
  const assumptionConfidence: CfoAssumption['confidence'] = confidence.includes('high')
    ? 'High'
    : confidence.includes('low')
      ? 'Low'
      : 'Medium';

  // DMD-1202 / DMD-1217: Key Assumptions payback must match header/CFO Payback (ROI model).
  const authoritativePayback = viewModel.header.payback;

  // Skip payback rows — already shown in the summary metrics strip.
  const assumptions: CfoAssumption[] = viewModel.metrics.financial
    .filter((row) => !/payback/i.test(row.label))
    .slice(0, 3)
    .map((row) => {
      const rawValue = row.target !== '—' ? row.target : row.current;
      return {
        label: row.label,
        value: rawValue,
        confidence: assumptionConfidence,
        source: row.baseline !== '—' ? `Baseline ${row.baseline}` : 'Initiative record',
      };
    });

  const hasInvestment =
    viewModel.metrics.implementationCost != null
    || (viewModel.header.investment !== '—' && viewModel.header.investment.trim() !== '');

  return {
    benefit: viewModel.header.roiRange,
    benefitNote: 'Base case',
    cost: hasInvestment ? viewModel.header.investment : 'Not available',
    costNote: hasInvestment ? 'Y1 cost' : 'Not set in ROI model',
    payback: authoritativePayback,
    conf: viewModel.header.confidence,
    assumptions,
    flags: cfoFlagsFromViewModel(viewModel),
  };
}
